import type { AchCatalog, AchText } from "../model/types";

/**
 * Арабские тексты публичного каталога достижений — поверх catalog.gen.ts, не меняя его.
 *
 * Каталог генерирует site_server (build_catalog → scripts/import-achievements.py) на пяти
 * языках: ru, en, de, es, zh; арабского у сервера пока нет. Поэтому арабский живёт рядом
 * (i18n/arCatalog*.ts) и подставляется только при языке "ar" (lib/lang.ts pickText,
 * lib/units.ts unitText); сам ACH_CATALOG остаётся ровно сгенерированным объектом
 * (cardModel.catalog.test: ACH_CATALOG === catalog.gen), остальные языки не меняются.
 * Когда арабский появится у сервера — эти файлы удалить, pickText возьмёт text.ar.
 *
 * Ключ — путь по стабильным ключам каталога (не по номерам — новая версия каталога
 * их сдвигает):
 *   rarity.<tier>.name · facets.<key> · spheres.<key>
 *   achievements.<key>.name | what | label · achievements.<key>.tiers.<tier>.label
 *   topics.<key>.name | topic | what | title · topics.<key>.tiers.<tier>.label
 *   topic_extras.<key>.name | what
 *   levels.spheres.<sphere>.general · levels.spheres.<sphere>.levels.<n>.name | approx
 *   levels.ladders.<ladder>.title | general | picture_note · levels.ladders.<ladder>.variant_names.<v>
 *   levels.ladders.<ladder>.levels.<n>.label | name
 * Значение — строка или [мужская, женская] (у названий и званий с женской формой → ar / ar_f).
 * Единицы — units.<unit>: формы {zero, one, two, few, many, other}, «{n}» — число.
 */
export type ArCatalogValue = string | readonly [string, string];

export type ArPluralForms = Readonly<Partial<Record<"zero" | "one" | "two" | "few" | "many" | "other", string>>>;

export interface ArCatalogData {
    readonly texts: Readonly<Record<string, ArCatalogValue>>;
    readonly units: Readonly<Record<string, ArPluralForms>>;
}

export type ArText = { readonly ar: string; readonly ar_f?: string };

/** Пути каталога, у которых есть текст: путь → текстовый объект каталога. */
export const catalogTextPaths = (catalog: AchCatalog): Map<string, AchText> => {
    const out = new Map<string, AchText>();
    const put = (path: string, text: AchText | undefined) => {
        if (text) out.set(path, text);
    };
    catalog.rarity.forEach((r) => put(`rarity.${r.tier}.name`, r.name));
    Object.entries(catalog.facets).forEach(([k, t]) => put(`facets.${k}`, t));
    Object.entries(catalog.spheres).forEach(([k, t]) => put(`spheres.${k}`, t));
    catalog.achievements.forEach((a) => {
        put(`achievements.${a.key}.name`, a.name);
        put(`achievements.${a.key}.what`, a.what);
        put(`achievements.${a.key}.label`, a.label);
        a.tiers?.forEach((t) => put(`achievements.${a.key}.tiers.${t.tier}.label`, t.label));
    });
    catalog.topics.forEach((t) => {
        put(`topics.${t.key}.name`, t.name);
        put(`topics.${t.key}.topic`, t.topic);
        put(`topics.${t.key}.what`, t.what);
        put(`topics.${t.key}.title`, t.title);
        t.tiers.forEach((x) => put(`topics.${t.key}.tiers.${x.tier}.label`, x.label));
    });
    catalog.topic_extras.forEach((x) => {
        put(`topic_extras.${x.key}.name`, x.name);
        put(`topic_extras.${x.key}.what`, x.what);
    });
    Object.entries(catalog.levels.spheres).forEach(([s, def]) => {
        if (!def) return;
        put(`levels.spheres.${s}.general`, def.general);
        def.levels.forEach((l) => {
            put(`levels.spheres.${s}.levels.${l.n}.name`, l.name);
            put(`levels.spheres.${s}.levels.${l.n}.approx`, l.approx);
        });
    });
    Object.entries(catalog.levels.ladders || {}).forEach(([k, def]) => {
        if (!def) return;
        put(`levels.ladders.${k}.title`, def.title);
        put(`levels.ladders.${k}.general`, def.general);
        put(`levels.ladders.${k}.picture_note`, def.picture_note);
        Object.entries(def.variant_names || {}).forEach(([v, t]) => put(`levels.ladders.${k}.variant_names.${v}`, t));
        def.levels.forEach((l) => {
            put(`levels.ladders.${k}.levels.${l.n}.label`, l.label);
            put(`levels.ladders.${k}.levels.${l.n}.name`, l.name);
        });
    });
    return out;
};

// ── Реестр: каталог (ленивый кусок, model/catalog.ts) отдаёт сюда свои арабские тексты ──

let source: { catalog: AchCatalog; data: ArCatalogData } | null = null;
let byText: WeakMap<object, ArText> | null = null;

/** Подключить арабские тексты каталога (model/catalog.ts). Сам каталог не меняется. */
export const registerArabicCatalog = (catalog: AchCatalog, data: ArCatalogData): void => {
    source = { catalog, data };
    byText = null;
};

/** Арабский текст объекта каталога (name, what, label…); нет — undefined. Индекс строится при первом вопросе. */
export const arabicCatalogText = (text: object): ArText | undefined => {
    if (!source) return undefined;
    if (!byText) {
        const map = new WeakMap<object, ArText>();
        const { texts } = source.data;
        catalogTextPaths(source.catalog).forEach((obj, path) => {
            const v = texts[path];
            if (v === undefined) return;
            map.set(obj, typeof v === "string" ? { ar: v } : { ar: v[0], ar_f: v[1] });
        });
        byText = map;
    }
    return byText.get(text);
};

/** Арабские формы единицы каталога; нет — undefined (unitText возьмёт английские). */
export const arabicCatalogUnit = (unit: string): ArPluralForms | undefined => source?.data.units[unit];
